/**
 * @format
 */
import { GuidelinesAndRequirementsService } from '@/scripts/services/mdf/guidelines-and-requirements-service';
import { ActivityTypesService } from '@/scripts/services/mdf/activity-types-service';
import { AccountsService } from '@/scripts/services/mdf/accounts-service';
import { RegisteredDealsService } from '../scripts/services/mdf/registered-deals-service';
import { CurrencyMixin } from '@/mixins/currency-mixin';
import { MDFClientRequestService } from '@/scripts/services/mdf/client/client-request-service';
import { MDFAgencyPreapprovedFundService } from '@/scripts/services/mdf/agency/agency-preapproved-fund-service';

export const MDFClientRequestFormMixin = {
    mixins: [CurrencyMixin],

    methods: {
        getGuidelinesAndRequirementsById(vendorId) {
            return new Promise((resolve, reject) => {
                GuidelinesAndRequirementsService.getById(vendorId)
                    .then(({ data }) => {
                        resolve(data.data);
                    })
                    .catch(reject);
            });
        },

        parseFilesArray(files) {
            return files.map(({ Id, Name, Size, Type, Url }) => {
                return {
                    id: Id,
                    name: Name,
                    type: Type,
                    size: Size,
                    downloadUrl: Url,
                };
            });
        },

        parseActivtyTypes(activityTypes) {
            const activityOptions = activityTypes.map((activity) => {
                return {
                    id: activity.Id,
                    name: activity.Type,
                };
            });

            return activityOptions;
        },

        getActivityTypesById(vendorId) {
            return new Promise((resolve, reject) => {
                ActivityTypesService.getById(vendorId)
                    .then(({ data }) => {
                        resolve(this.parseActivtyTypes(data.data));
                    })
                    .catch(reject);
            });
        },

        parseCurrency(amount) {
            const formattedValue = amount.toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD',
            });

            const integerPart = formattedValue.split('.')[0];
            return integerPart;
        },

        parseDate(date) {
            return this.$moment(date).startOf('day').toISOString();
        },

        parseContactsToParticipants(contacts) {
            const participants = contacts.map(({ name, profilePicture, id }) => {
                return {
                    ParticipantId: id,
                    ParticipantType: 'Contact',
                    Name: name,
                    ProfileImageUrl: profilePicture,
                };
            });

            return participants;
        },

        parseMDFRequiredInformationToPayload(accountId, agencyId, requiredInformation) {
            const {
                fundTitle,
                activityType,
                totalCostOfActivity,
                preferredCurrency,
                activityDate,
                mdfRequestAmount,
                contacts,
                targetAudience,
                expectedNumberOfAttendees,
                estimatedNumberOfLeads,
                projectedROI,
                requestNotes,
            } = requiredInformation;

            const payload = {
                Name: fundTitle,
                ClientAccountId: accountId,
                AgencyAccountId: Number(agencyId),
                ActivityType: activityType ? activityType.name : null,
                TotalAmountSelectedCurrency: Number(totalCostOfActivity),
                CurrencyCode: preferredCurrency.isoCode,
                CurrencySymbol: this.getFlag(preferredCurrency.isoCode).symbol,
                ActivityDate: this.parseDate(activityDate),
                RequestedAmountSelectedCurrency: Number(mdfRequestAmount),
                Participants: this.parseContactsToParticipants(contacts),
                TargetAudience: targetAudience && String(targetAudience),
                ExpectedAttendance: Number(expectedNumberOfAttendees),
                EstimatedNumberOfLeads: Number(estimatedNumberOfLeads),
                ProjectedRoi: Number(projectedROI),
                RequestNotes: requestNotes,
            };

            return payload;
        },

        parseDealStatus(isApproved) {
            if (isApproved === true) {
                return 'approved';
            }
            if (isApproved === false) {
                return 'denied';
            }
            return 'pending';
        },

        async parseDeals(deals) {
            const parsedDeals = await Promise.all(
                deals.map(
                    async ({
                        Name,
                        RegistrationNumber,
                        Value,
                        IsApproved,
                        Description,
                        AgencyId,
                    }) => {
                        const { data } =
                            await RegisteredDealsService.getCompanyByAgencyId(AgencyId);
                        const companyName = data.value[0].Name;

                        return {
                            action: Name,
                            company: companyName,
                            dealNumber: RegistrationNumber,
                            description: Description,
                            status: this.parseDealStatus(IsApproved),
                            amount: this.parseCurrency(Value),
                        };
                    },
                ),
            );

            return parsedDeals;
        },

        async parseRegisteredDeals(registeredDeals, dealName) {
            return [
                {
                    dealNamePack: dealName,
                    dealRequests: await this.parseDeals(registeredDeals),
                },
            ];
        },

        async parseWonDeals(wonDeals) {
            const parsedWonDeals = await Promise.all(
                wonDeals.map(async ({ Name, RegisteredDeals }) => ({
                    dealNamePack: Name,
                    dealRequests: await this.parseDeals(RegisteredDeals),
                })),
            );

            return parsedWonDeals;
        },

        postMDFRequest(accountId, payload) {
            return new Promise((resolve, reject) => {
                MDFClientRequestService.post(accountId, payload)
                    .then(({ data }) => {
                        resolve(data);
                    })
                    .catch(reject);
            });
        },

        async getRegisteredDealsById(mdfId) {
            try {
                const { data } = await RegisteredDealsService.getRegisteredDealsByMdfId(mdfId);
                const parsedRegisteredDeals = await this.parseRegisteredDeals(data.value);
                return parsedRegisteredDeals;
            } catch (error) {
                return Promise.reject(error);
            }
        },

        async getWonDealsById(mdfId) {
            try {
                const { data } = await RegisteredDealsService.getWonDealsByMdfId(mdfId);
                const parsedWonDeals = await this.parseWonDeals(data.value);
                return parsedWonDeals;
            } catch (error) {
                return Promise.reject(error);
            }
        },

        getAgencyById(accountId) {
            return new Promise((resolve, reject) => {
                AccountsService.getAgency(accountId)
                    .then(({ data }) => {
                        resolve(data.data);
                    })
                    .catch(reject);
            });
        },

        getPreapprovedFundsData(accountId, agencyAccountId) {
            return new Promise((resolve, reject) => {
                MDFAgencyPreapprovedFundService.getBalance(accountId, agencyAccountId)
                    .then(({ data }) => {
                        resolve(data.data.balanceUSD);
                    })
                    .catch(reject);
            });
        },

        async getRegisteredDealsByPartner(partnerId) {
            try {
                const { data } =
                    await RegisteredDealsService.getRegisteredDealsByPartnerId(partnerId);
                const parsedRegisteredDeals = await this.parseRegisteredDeals(data.value);
                return parsedRegisteredDeals;
            } catch (error) {
                return Promise.reject(error);
            }
        },

        async getWonDealsByPartner(partnerId) {
            try {
                const { data } = await RegisteredDealsService.getWonDealsByPartnerId(partnerId);
                const parsedWonDeals = await this.parseWonDeals(data.value);
                return parsedWonDeals;
            } catch (error) {
                return Promise.reject(error);
            }
        },
    },
};
